'use client'
import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { ArrowRight, Users, Star, Zap, Trophy, Settings } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { AnimatedButtonLink } from '@/components/ui/animated-button-link'
import Image from 'next/image'
import { HeroHeader } from './header'
import dynamic from 'next/dynamic'

// Dynamic imports for heavy components that were blocking
const TextEffect = dynamic(() => import('@/components/ui/text-effect').then(mod => ({ default: mod.TextEffect })), {
  ssr: false,
  loading: () => <span className="contents" />
})
const AnimatedGroup = dynamic(() => import('@/components/ui/animated-group').then(mod => ({ default: mod.AnimatedGroup })), {
  ssr: false,
  loading: () => <div className="contents" />
})
const Spotlight = dynamic(() => import('@/components/ui/spotlight-new').then(mod => ({ default: mod.Spotlight })), {
  ssr: false,
  loading: () => null
})

// Lazy load heavy components that appear below the fold with loading states
const LogoCloud = dynamic(() => import('@/components/logo-cloud'), {
  ssr: false,
  loading: () => <div className="h-32 bg-gray-100 animate-pulse rounded-lg" />
})
const BentoDndFeaturesSection = dynamic(() => import('@/components/bento-dnd-features'), {
  ssr: false,
  loading: () => <div className="h-96 bg-gray-100 animate-pulse rounded-lg" />
})
const Footer = dynamic(() => import('@/components/footer'), {
  ssr: false,
  loading: () => <div className="h-64 bg-gray-100 animate-pulse" />
})
const Marquee3DReviews = dynamic(() => import('@/components/marquee-3d-reviews').then(mod => ({ default: mod.Marquee3DReviews })), {
  ssr: false,
  loading: () => <div className="h-96 bg-gray-100 animate-pulse rounded-lg" />
})
const AnimatedGalleryContainerScroll = dynamic(() => import('@/components/blocks/animated-gallery').then(mod => ({ default: mod.ContainerScroll })), {
  ssr: false,
  loading: () => <div className="h-96 bg-gray-100 animate-pulse rounded-lg" />
})

const ContainerSticky = dynamic(() => import('@/components/blocks/animated-gallery').then(mod => ({ default: mod.ContainerSticky })), { ssr: false })
const GalleryCol = dynamic(() => import('@/components/blocks/animated-gallery').then(mod => ({ default: mod.GalleryCol })), { ssr: false })
const GalleryContainer = dynamic(() => import('@/components/blocks/animated-gallery').then(mod => ({ default: mod.GalleryContainer })), { ssr: false })
const CallToAction = dynamic(() => import('@/components/call-to-action'), {
  ssr: false,
  loading: () => <div className="h-64 bg-gray-100 animate-pulse rounded-lg" />
})
const FeatureCarousel = dynamic(() => import('@/components/ui/feature-carousel'), {
  ssr: false,
  loading: () => <div className="h-96 bg-gray-100 animate-pulse rounded-lg" />
})
const FaqAccordion = dynamic(() => import('@/components/ui/faq-chat-accordion').then(mod => ({ default: mod.FaqAccordion })), {
  ssr: false,
  loading: () => <div className="h-64 bg-gray-100 animate-pulse rounded-lg" />
})

const faqData = [
  {
    id: 1,
    question: "How does SocialLoop help me network better?",
    answer:
      "SocialLoop uses AI to match you with the right people based on your interests, goals, and compatibility. Instead of random small talk, you get meaningful connections through interactive games and smart recommendations that break the ice naturally.",
  },
  {
    id: 2,
    question: "Do I need to download an app to join events?",
    answer:
      "No! SocialLoop works directly in your browser. Simply scan a QR code or enter an event code to instantly join any networking experience. No lengthy downloads or registrations required.",
  },
  {
    id: 3,
    question: "How does the AI matching actually work?",
    answer:
      "Our AI analyzes your professional background, interests, networking goals, and personality through fun activities. It then identifies people with complementary skills, shared interests, or mutual networking benefits and suggests when and how to connect.",
  },
  {
    id: 4,
    question: "Is my personal information safe and private?",
    answer:
      "Absolutely! We only use the information you choose to share for matching purposes. Your data is encrypted, never sold to third parties, and you control what others can see about you. You can update or delete your profile anytime.",
  },
  {
    id: 5,
    question: "What if I'm shy or not good at networking?",
    answer:
      "SocialLoop is perfect for introverts! Our interactive games and conversation starters eliminate awkward small talk. The AI provides suggested talking points and helps you find people with shared interests, making every conversation more natural and engaging.",
  },
]


import { cn } from '@/lib/utils'

// More components
// Render PhotoGallery on the server so Next can prioritize above-the-fold images
const PhotoGallery = dynamic(() => import('@/components/ui/gallery').then(mod => ({ default: mod.PhotoGallery })), { ssr: true })
const DotPattern = dynamic(() => import('@/components/magicui/dot-pattern').then(mod => ({ default: mod.DotPattern })), { ssr: false })
const NeubrutalistFeedbackCard = dynamic(() => import('@/components/ui/feedback-card').then(mod => ({ default: mod.NeubrutalistFeedbackCard })), { ssr: false })


const transitionVariants = {
    item: {
        hidden: {
            opacity: 0,
            filter: 'blur(12px)',
            y: 12,
        },
        visible: {
            opacity: 1,
            filter: 'blur(0px)',
            y: 0,
            transition: {
                type: 'spring',
                bounce: 0.3,
                duration: 1.5,
            },
        },
    },
}

const IMAGES_1 = [
    "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Business networking event
    "https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Conference networking
    "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Professional event
    "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Business meeting
]

const IMAGES_2 = [
    "https://images.unsplash.com/photo-1560439514-4e9645039924?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Team collaboration
    "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Business networking
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Conference room
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Professional handshake
]

const IMAGES_3 = [
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Business event
    "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Startup networking
    "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Professional meeting
    "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3", // Business collaboration
]

// Create actual image objects for FeatureCarousel with real images
const createImage = (src: string, width: number = 800, height: number = 400) => ({
    src,
    height,
    width,
})

const networkingImages = {
    step1dark1: createImage("https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Join Event
    step1dark2: createImage("https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // QR Code Scan
    step1light1: createImage("https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Join Event
    step1light2: createImage("https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // QR Code Scan
    step2dark1: createImage("https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Set Profile
    step2dark2: createImage("https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // AI Matching
    step2light1: createImage("https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Set Profile
    step2light2: createImage("https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // AI Matching
    step3dark: createImage("https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Play & Connect
    step3light: createImage("https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&h=300&auto=format&fit=crop&q=60", 600, 300), // Play & Connect
    step4light: createImage("https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=700&h=350&auto=format&fit=crop&q=60", 700, 350), // Network Success
    alt: "Networking process steps"
}

export default function HeroSection() {
    const [showFeedback, setShowFeedback] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            // Show feedback button when user scrolls past the "How It Works" section
            const scrollPosition = window.scrollY
            const windowHeight = window.innerHeight
            const showThreshold = windowHeight * 2.2 // Show after user scrolls past gallery and logo sections

            setShowFeedback(scrollPosition > showThreshold)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <div className="w-full bg-background overflow-x-hidden">
            {/* Scattered Decorative Elements - Hidden on mobile for better performance */}
            <div className="fixed inset-0 pointer-events-none z-[5] hidden md:block overflow-hidden">
                {/* Colorful geometric shapes scattered around */}
                <div className="absolute top-20 left-10 w-8 h-8 bg-pink-400 border-2 border-black transform rotate-45 shadow-lg"></div>
                <div className="absolute top-40 right-20 w-6 h-6 bg-yellow-400 border-2 border-black rounded-full shadow-lg"></div>
                <div className="absolute top-60 left-1/4 w-5 h-12 bg-cyan-400 border-2 border-black transform -rotate-12 shadow-lg"></div>
                <div className="absolute top-80 right-1/3 w-10 h-6 bg-green-400 border-2 border-black transform rotate-12 shadow-lg"></div>
                <div className="absolute top-96 left-1/2 w-7 h-7 bg-purple-400 border-2 border-black transform rotate-45 shadow-lg"></div>

                {/* Sticky note testimonials - Simplified for mobile */}
                <div className="absolute top-32 right-4 md:right-8 bg-yellow-300 border-2 border-black p-2 md:p-3 transform rotate-12 shadow-lg max-w-28 md:max-w-32">
                    <p className="text-xs font-bold text-black">"Best networking app ever!"</p>
                    <div className="text-xs text-black/70 mt-1 hidden sm:block">- Sarah K.</div>
                </div>

                <div className="absolute top-72 left-4 md:left-8 bg-pink-300 border-2 border-black p-2 md:p-3 transform -rotate-8 shadow-lg max-w-32 md:max-w-36 hidden sm:block">
                    <p className="text-xs font-bold text-black">"Made 15 connections in one event!"</p>
                    <div className="text-xs text-black/70 mt-1">- Mike R.</div>
                </div>

                <div className="absolute top-[500px] right-4 md:right-12 bg-green-300 border-2 border-black p-2 md:p-3 transform rotate-6 shadow-lg max-w-28 md:max-w-32 hidden lg:block">
                    <p className="text-xs font-bold text-black">"So much fun!"</p>
                    <div className="text-xs text-black/70 mt-1">- Alex T.</div>
                </div>

                {/* Hand-drawn style arrows - Hidden on mobile for cleaner look */}
                <div className="absolute top-48 left-1/3 transform -rotate-12 hidden lg:block">
                    <svg width="60" height="40" viewBox="0 0 60 40" className="text-black">
                        <path d="M5 20 Q30 5 50 15 L45 10 M50 15 L45 20" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        <text x="15" y="35" className="text-xs font-bold fill-current">Try it!</text>
                    </svg>
                </div>

                <div className="absolute top-[600px] right-1/4 transform rotate-45 hidden lg:block">
                    <svg width="50" height="50" viewBox="0 0 50 50" className="text-red-600">
                        <path d="M10 25 Q25 10 40 25 L35 20 M40 25 L35 30" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        <text x="5" y="45" className="text-xs font-bold fill-current">Cool!</text>
                    </svg>
                </div>

                {/* More scattered elements */}
                <div className="absolute top-[800px] left-16 w-4 h-4 bg-orange-400 border-2 border-black transform rotate-45 shadow-lg"></div>
                <div className="absolute top-[900px] right-24 w-8 h-4 bg-blue-400 border-2 border-black transform -rotate-6 shadow-lg"></div>
                <div className="absolute top-[1000px] left-1/3 w-6 h-6 bg-red-400 border-2 border-black rounded-full shadow-lg"></div>
            </div>

            {/* Main content wrapper with relative positioning and higher z-index */}
            <div className="relative z-20 min-h-dvh bg-background overflow-x-hidden ">
                <HeroHeader py-8/>
                <main className="relative overflow-hidden overflow-x-hidden ">
                {/* Enhanced Brand-Consistent Spotlight Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    <Spotlight
                        gradientFirst="radial-gradient(68.54% 68.72% at 55.02% 31.46%, rgba(30, 116, 230, 0.12) 0%, rgba(30, 116, 230, 0.08) 30%, rgba(30, 116, 230, 0.04) 60%, rgba(30, 116, 230, 0) 80%)"
                        gradientSecond="radial-gradient(50% 50% at 50% 50%, rgba(23, 203, 191, 0.08) 0%, rgba(23, 203, 191, 0.05) 40%, rgba(23, 203, 191, 0.02) 80%, transparent 100%)"
                        gradientThird="radial-gradient(50% 50% at 50% 50%, rgba(96, 165, 250, 0.06) 0%, rgba(96, 165, 250, 0.03) 50%, rgba(96, 165, 250, 0.01) 80%, transparent 100%)"
                        translateY={-250}
                        width={400}
                        height={1000}
                        smallWidth={160}
                        duration={12}
                        xOffset={40}
                    />

                    {/* Optimized Floating Particles (deterministic to avoid hydration mismatch) */}
                    {Array.from({ length: 6 }).map((_, i) => {
                        const colors = ['#17cbbf', '#60a5fa'];
                        // Simple deterministic pseudo-random based on index
                        const rand = (seed: number) => {
                            const x = Math.sin(seed) * 10000;
                            return x - Math.floor(x);
                        };
                        const left = 20 + rand(i * 13.37) * 60;
                        const top = 20 + rand(i * 7.11) * 60;
                        const delay = rand(i * 3.33) * 3;
                        const duration = 3 + rand(i * 5.55) * 2;
                        return (
                            <div
                                key={i}
                                className="absolute w-1 h-1 rounded-full animate-pulse"
                                style={{
                                    backgroundColor: colors[i % colors.length],
                                    opacity: 0.4,
                                    left: `${left}%`,
                                    top: `${top}%`,
                                    animationDelay: `${delay}s`,
                                    animationDuration: `${duration}s`
                                }}
                            />
                        );
                    })}

                    {/* Simplified Background Gradient for Depth */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#1e74e6]/3 via-transparent to-[#17cbbf]/3 pointer-events-none" />
                </div>
                
                {/* Move DotPattern outside the container to higher z-index */}
                <DotPattern
                    width={16}
                    height={16}
                    cx={1}
                    cy={1}
                    cr={3}
                    glow={false}
                    className={cn(
                        "opacity-40 z-[0] absolute inset-0",
                        "[mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
                    )}
                    style={{color: '#1e74e6'}}
                />
                <section className="min-h-[60vh] sm:min-h-[80vh] lg:min-h-screen flex items-center justify-center pt-8 sm:pt-12 lg:pt-16 bg-blue-50 px-4 sm:px-6 lg:px-8">
                    <div className="relative w-full">
                        <AnimatedGroup
                            initialVisible
                            variants={{
                                container: {
                                    visible: {
                                        transition: {
                                            delayChildren: 0,
                                        },
                                    },
                                },
                                item: {
                                    hidden: {
                                        opacity: 0,
                                        y: 8,
                                    },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        transition: {
                                            type: 'spring',
                                            bounce: 0.2,
                                            duration: 0.3,
                                        },
                                    },
                                },
                            }}
                            className="absolute inset-0 -z-20">
                            <div className="absolute inset-x-0 top-56 -z-20 hidden lg:top-32 dark:block">
                                <div className="w-full h-full opacity-20" style={{background: 'linear-gradient(to bottom right, #1e74e6, #17cbbf, #60a5fa)'}}></div>
                            </div>
                        </AnimatedGroup>
                        <div className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--color-background)_75%)]"></div>
                        <div className="mx-auto max-w-7xl">
                            <div className="text-center sm:mx-auto lg:mr-auto lg:mt-0">


                                {/* PhotoGallery Component */}
                                <PhotoGallery animationDelay={0} />
                            </div>
                        </div>


                    </div>
                </section>
            </main>
            
                {/* Animated Gallery Section - Visual showcase with context */}
                <section className="relative pt-12 pb-0 bg-yellow-100">
                    {/* Floating sticker-like badges - hidden on mobile to reduce clutter */}
                    <div className="absolute top-8 left-8 bg-yellow-400 border-3 border-black px-3 py-2 transform rotate-12 shadow-xl z-[5] pointer-events-none hidden md:block">
                        <span className="text-xs font-black text-black">REAL EVENTS!</span>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 border border-black rounded-full"></div>
                    </div>

                    <div className="absolute top-16 right-12 bg-green-400 border-3 border-black px-3 py-2 transform -rotate-8 shadow-xl z-[5] pointer-events-none hidden md:block">
                        <span className="text-xs font-black text-black">100% AUTHENTIC</span>
                        <div className="absolute -bottom-1 -left-1 w-4 h-2 bg-blue-400 border border-black transform rotate-45"></div>
                    </div>

                    <div className="absolute bottom-20 left-16 bg-pink-400 border-3 border-black px-3 py-2 transform rotate-6 shadow-xl z-[5] pointer-events-none hidden md:block">
                        <span className="text-xs font-black text-black">NETWORKING MAGIC</span>
                        <div className="absolute -top-1 right-2 w-2 h-4 bg-purple-400 border border-black transform -rotate-12"></div>
                    </div>

                    {/* More scattered geometric elements - hidden on mobile to reduce clutter */}
                    <div className="absolute top-32 left-1/4 w-6 h-6 bg-cyan-400 border-2 border-black transform rotate-45 shadow-lg pointer-events-none hidden md:block"></div>
                    <div className="absolute bottom-32 right-1/4 w-8 h-4 bg-orange-400 border-2 border-black transform -rotate-12 shadow-lg pointer-events-none hidden md:block"></div>

                    <div className="text-center mb-8 relative">
                        <AnimatedGroup variants={{ item: transitionVariants }}>
                            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 border-4 border-black bg-[#facc15] text-black font-black uppercase tracking-wide transform -rotate-2 hover:rotate-0 transition-transform duration-200 shadow-[4px_4px_0_0_#000]">
                                <Users className="h-4 w-4" />
                                <span>Real Events, Real Connections</span>
                            </div>
                        </AnimatedGroup>
                        <div className="relative flex flex-col items-center gap-3">
                            {/* Neubrutalist title chip */}
                            <div className="inline-block bg-[#facc15] border-4 border-black px-4 py-2 transform rotate-1 hover:rotate-0 transition-transform duration-200 shadow-[4px_4px_0px_0px_#000]">
                                <h2 className="text-3xl md:text-4xl font-black text-black m-0">
                                    See Networking in Action
                                </h2>
                            </div>
                            {/* Neubrutalist subtitle chip */}
                            <div className="inline-block bg-[#1553c5] border-4 border-black px-4 py-2 transform -rotate-1 shadow-[4px_4px_0px_0px_#000]">
                                <p className="text-base md:text-lg font-semibold text-white m-0">
                                    Real moments from events powered by SocialLoop – where meaningful connections happen naturally
                                </p>
                            </div>
                            {/* Accents */}
                            <div className="absolute -top-3 -right-3 w-4 h-4 border-2 border-black rotate-45 bg-pink-400" />
                            <div className="absolute -bottom-3 -left-3 w-3 h-3 border-2 border-black rounded-full bg-green-400" />
                        </div>
                    </div>
                    
                    <AnimatedGalleryContainerScroll className="relative h-[120vh] sm:h-[150vh]">
                        <ContainerSticky>
                            <GalleryContainer className="p-4 sm:p-6 overflow-hidden">
                                <GalleryCol yRange={["-10%", "2%"]} mobileYRange={["-12%", "6%"]} className="-mt-2">
                                    {IMAGES_1.map((imageUrl, index) => (
                                        <div
                                            key={index}
                                            className={`relative border-4 border-black shadow-xl mb-1 sm:mb-2 ${
                                                index % 4 === 0
                                                    ? "bg-[#1e74e6] transform rotate-2 hover:bg-black hover:text-white"
                                                    : index % 4 === 1
                                                        ? "bg-[#17cbbf] transform -rotate-1 hover:bg-black hover:text-white"
                                                        : index % 4 === 2
                                                            ? "bg-[#60a5fa] transform rotate-3 hover:bg-black hover:text-white"
                                                            : "bg-[#facc15] transform -rotate-2 hover:bg-black hover:text-white"
                                            } hover:rotate-0 transition-all duration-200 p-1 sm:p-3`}
                                        >
                                            <Image
                                                className="aspect-square sm:aspect-video block h-auto max-h-full w-full object-cover border-2 border-black"
                                                src={imageUrl}
                                                alt="Professional networking event - people connecting and collaborating"
                                                width={400}
                                                height={300}
                                                loading={index < 2 ? "eager" : "lazy"}
                                                quality={85}
                                                placeholder="blur"
                                                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                                            />
                                            {/* Decorative corner element - smaller on mobile */}
                                            <div className={`absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 border-2 border-black transform rotate-45 ${
                                                index % 4 === 0
                                                    ? "bg-cyan-400"
                                                    : index % 4 === 1
                                                        ? "bg-pink-400"
                                                        : index % 4 === 2
                                                            ? "bg-purple-400"
                                                            : "bg-orange-400"
                                            }`}></div>
                                        </div>
                                    ))}
                                </GalleryCol>
                                <GalleryCol className="mt-[-20%] sm:mt-[-50%]" yRange={["15%", "5%"]} mobileYRange={["5%", "-2%"]}>
                                    {IMAGES_2.map((imageUrl, index) => (
                                        <div
                                            key={index}
                                            className={`relative border-4 border-black shadow-xl mb-1 sm:mb-2 ${
                                                index % 4 === 0
                                                    ? "bg-[#17cbbf] transform -rotate-3 hover:bg-black hover:text-white"
                                                    : index % 4 === 1
                                                        ? "bg-[#facc15] transform rotate-2 hover:bg-black hover:text-white"
                                                        : index % 4 === 2
                                                            ? "bg-[#1e74e6] transform -rotate-1 hover:bg-black hover:text-white"
                                                            : "bg-[#60a5fa] transform rotate-3 hover:bg-black hover:text-white"
                                            } hover:rotate-0 transition-all duration-200 p-1 sm:p-3`}
                                        >
                                            <Image
                                                className="aspect-square sm:aspect-video block h-auto max-h-full w-full object-cover border-2 border-black"
                                                src={imageUrl}
                                                alt="Professional networking event - people connecting and collaborating"
                                                width={400}
                                                height={300}
                                                loading={index < 2 ? "eager" : "lazy"}
                                                quality={85}
                                                placeholder="blur"
                                                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                                            />
                                            {/* Decorative elements - smaller on mobile */}
                                            <div className={`absolute -top-1 -left-1 sm:-top-2 sm:-left-2 w-4 h-4 sm:w-6 sm:h-6 border-2 border-black rounded-full ${
                                                index % 4 === 0
                                                    ? "bg-yellow-400"
                                                    : index % 4 === 1
                                                        ? "bg-green-400"
                                                        : index % 4 === 2
                                                            ? "bg-blue-400"
                                                            : "bg-red-400"
                                            }`}></div>
                                            <div className={`absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-3 h-3 sm:w-5 sm:h-5 border-2 border-black transform rotate-45 ${
                                                index % 4 === 0
                                                    ? "bg-lime-400"
                                                    : index % 4 === 1
                                                        ? "bg-rose-400"
                                                        : index % 4 === 2
                                                            ? "bg-indigo-400"
                                                            : "bg-amber-400"
                                            }`}></div>
                                        </div>
                                    ))}
                                </GalleryCol>
                                <GalleryCol yRange={["-10%", "2%"]} mobileYRange={["-12%", "6%"]} className="-mt-2 hidden sm:flex">
                                    {IMAGES_3.map((imageUrl, index) => (
                                        <div
                                            key={index}
                                            className={`relative border-4 border-black shadow-xl mb-1 sm:mb-2 ${
                                                index % 4 === 0
                                                    ? "bg-[#60a5fa] transform rotate-1 hover:bg-black hover:text-white"
                                                    : index % 4 === 1
                                                        ? "bg-[#1e74e6] transform -rotate-2 hover:bg-black hover:text-white"
                                                        : index % 4 === 2
                                                            ? "bg-[#17cbbf] transform rotate-3 hover:bg-black hover:text-white"
                                                            : "bg-[#facc15] transform -rotate-1 hover:bg-black hover:text-white"
                                            } hover:rotate-0 transition-all duration-200 p-1 sm:p-3`}
                                        >
                                            <Image
                                                className="aspect-square sm:aspect-video block h-auto max-h-full w-full object-cover border-2 border-black"
                                                src={imageUrl}
                                                alt="Professional networking event - people connecting and collaborating"
                                                width={400}
                                                height={300}
                                                loading={index < 2 ? "eager" : "lazy"}
                                                quality={85}
                                                placeholder="blur"
                                                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                                            />
                                            {/* Triple decorative elements for variety */}
                                            <div className={`absolute -top-3 -right-3 w-7 h-7 border-2 border-black transform rotate-45 ${
                                                index % 4 === 0
                                                    ? "bg-red-400"
                                                    : index % 4 === 1
                                                        ? "bg-blue-400"
                                                        : index % 4 === 2
                                                            ? "bg-yellow-400"
                                                            : "bg-green-400"
                                            }`}></div>
                                            <div className={`absolute -bottom-2 -left-2 w-4 h-4 border-2 border-black rounded-full ${
                                                index % 4 === 0
                                                    ? "bg-purple-400"
                                                    : index % 4 === 1
                                                        ? "bg-orange-400"
                                                        : index % 4 === 2
                                                            ? "bg-pink-400"
                                                            : "bg-cyan-400"
                                            }`}></div>
                                            <div className={`absolute top-1/2 -left-3 w-3 h-8 border-2 border-black transform -rotate-12 ${
                                                index % 4 === 0
                                                    ? "bg-teal-400"
                                                    : index % 4 === 1
                                                        ? "bg-violet-400"
                                                        : index % 4 === 2
                                                            ? "bg-emerald-400"
                                                            : "bg-fuchsia-400"
                                            }`}></div>
                                        </div>
                                    ))}
                                </GalleryCol>
                            </GalleryContainer>
                        </ContainerSticky>
                    </AnimatedGalleryContainerScroll>
                </section>
                
                {/* Enhanced Section Divider with Torn Paper Effect */}
                <div className="relative py-8">
                    <div className="w-full h-8 bg-yellow-400 border-t-4 border-b-4 border-black relative overflow-hidden">
                        <div className="absolute inset-0 bg-black opacity-10" style={{
                            clipPath: 'polygon(0 0, 100% 0, 98% 30%, 95% 60%, 97% 80%, 100% 100%, 0 100%, 2% 80%, 5% 60%, 3% 30%)'
                        }}></div>
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className="bg-red-400 border-2 border-black px-4 py-1 transform rotate-2 shadow-lg">
                                <span className="text-xs font-black text-black">SCROLL FOR MORE!</span>
                            </div>
                        </div>
                        {/* Scattered mini elements on divider */}
                        <div className="absolute left-20 top-1 w-3 h-3 bg-pink-400 border border-black rounded-full"></div>
                        <div className="absolute right-24 top-2 w-2 h-4 bg-green-400 border border-black transform rotate-45"></div>
                        <div className="absolute left-1/3 bottom-1 w-4 h-2 bg-blue-400 border border-black transform -rotate-12"></div>
                    </div>
                </div>
                
                <LogoCloud />
                
                {/* Interactive How It Works Section */}
                <section className="py-16 md:py-24 bg-cyan-100 relative overflow-hidden">
                    {/* More scattered elements for this section */}
                    <div className="absolute top-10 left-10 w-6 h-6 bg-lime-400 border-2 border-black transform rotate-45 shadow-lg"></div>
                    <div className="absolute top-20 right-16 w-8 h-4 bg-rose-400 border-2 border-black transform -rotate-12 shadow-lg"></div>
                    <div className="absolute bottom-20 left-20 w-5 h-5 bg-indigo-400 border-2 border-black rounded-full shadow-lg"></div>
                    <div className="absolute bottom-32 right-12 w-7 h-3 bg-amber-400 border-2 border-black transform rotate-6 shadow-lg"></div>

                    {/* Floating sticky note with tip */}
                    <div className="absolute top-32 right-8 bg-cyan-300 border-2 border-black p-3 transform rotate-8 shadow-lg max-w-40 hidden lg:block">
                        <p className="text-xs font-bold text-black">"Pro tip: Complete your profile for better matches!"</p>
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-cyan-300 border-l-2 border-b-2 border-black transform rotate-45"></div>
                    </div>

                    <div className="mx-auto max-w-7xl px-6">
                        <div className="text-center mb-16 relative">
                            <AnimatedGroup variants={{
                                item: {
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
                                }
                            }}>
                                <div className="bg-orange-400 border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                                    <div className="flex items-center font-black text-black text-sm">
                                        <Settings className="mr-2 h-4 w-4" />
                                        INTERACTIVE GUIDE
                                    </div>
                                </div>
                            </AnimatedGroup>
                            <div className="bg-purple-400 border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-4">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-black mb-0">
                                    YOUR NETWORKING JOURNEY
                                </h2>
                            </div>
                            <div className="bg-cyan-400 border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-2xl mx-auto">
                                <p className="text-lg font-semibold text-black">
                                    Experience the step-by-step process of connecting with the right people at any event
                                </p>
                            </div>
                        </div>

                        <div className="max-w-4xl mx-auto relative">
                            {/* Decorative elements around the carousel */}
                            <div className="absolute -top-8 -left-8 w-12 h-6 bg-purple-400 border-2 border-black transform rotate-12 shadow-lg hidden md:block"></div>
                            <div className="absolute -top-4 -right-12 w-8 h-8 bg-orange-400 border-2 border-black rounded-full shadow-lg hidden md:block"></div>
                            <div className="absolute -bottom-6 -left-6 w-6 h-10 bg-green-400 border-2 border-black transform -rotate-6 shadow-lg hidden md:block"></div>
                            <div className="absolute -bottom-8 -right-8 w-10 h-5 bg-pink-400 border-2 border-black transform rotate-45 shadow-lg hidden md:block"></div>

                            {/* Hand-drawn arrow pointing to carousel */}
                            <div className="absolute -top-16 left-1/4 transform -rotate-12 hidden lg:block">
                                <svg width="80" height="50" viewBox="0 0 80 50" className="text-red-600">
                                    <path d="M10 30 Q40 10 65 25 L60 20 M65 25 L60 30" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                                    <text x="20" y="45" className="text-xs font-bold fill-current">Interactive!</text>
                                </svg>
                            </div>

                            <FeatureCarousel
                                title="Start Networking in Minutes"
                                description="Join an event, set your preferences, engage with interactive features, and make meaningful connections through our AI-powered platform. Click through each step to see how easy networking can be."
                                image={networkingImages}
                            />
                        </div>
                    </div>
                </section>
                
                {/* Zigzag Section Divider */}
                <div className="relative py-6">
                    <div className="w-full h-6 bg-gradient-to-r from-blue-400 via-green-400 to-purple-400 relative">
                        <div className="absolute inset-0 border-t-4 border-b-4 border-black"></div>
                        <div className="absolute inset-0" style={{
                            background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)'
                        }}></div>
                        {/* Mini decorative elements on zigzag */}
                        <div className="absolute left-1/4 -top-2 w-4 h-4 bg-yellow-400 border-2 border-black transform rotate-45 shadow-lg"></div>
                        <div className="absolute right-1/3 -bottom-2 w-3 h-6 bg-red-400 border-2 border-black transform -rotate-12 shadow-lg"></div>
                        <div className="absolute left-1/2 -top-1 w-5 h-3 bg-pink-400 border-2 border-black transform rotate-6 shadow-lg"></div>
                    </div>
                </div>

                {/* FAQ Section */}
                <FaqAccordion data={faqData} />
                
                {/* Creative Section Divider with Overlapping Elements */}
                <div className="relative py-8 overflow-hidden">
                    <div className="flex items-center justify-center space-x-4">
                        <div className="w-20 h-2 bg-pink-400 border-2 border-black transform rotate-2 shadow-lg"></div>
                        <div className="bg-cyan-400 border-4 border-black px-6 py-3 transform -rotate-1 shadow-xl">
                            <span className="text-sm font-black text-black">WHAT PEOPLE SAY</span>
                        </div>
                        <div className="w-20 h-2 bg-green-400 border-2 border-black transform -rotate-2 shadow-lg"></div>
                    </div>

                    {/* Overlapping decorative circles */}
                    <div className="absolute left-1/4 top-4 w-8 h-8 bg-yellow-400 border-2 border-black rounded-full shadow-lg opacity-80"></div>
                    <div className="absolute left-1/4 top-6 w-6 h-6 bg-red-400 border-2 border-black rounded-full shadow-lg"></div>

                    <div className="absolute right-1/4 bottom-4 w-10 h-6 bg-purple-400 border-2 border-black transform rotate-12 shadow-lg opacity-80"></div>
                    <div className="absolute right-1/4 bottom-2 w-6 h-8 bg-orange-400 border-2 border-black transform -rotate-6 shadow-lg"></div>

                    {/* Floating testimonial snippet */}
                    <div className="absolute top-0 right-8 bg-lime-300 border-2 border-black p-2 transform rotate-8 shadow-lg max-w-24 hidden xl:block">
                        <p className="text-xs font-bold text-black">"Amazing!"</p>
                    </div>
                </div>

                <Marquee3DReviews />
                
                {/* Draggable Features Section - Back in original position */}
                <BentoDndFeaturesSection />

                {/* Floating Feedback Button - Only visible in lower sections, mobile optimized */}
                <div className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 transition-all duration-300 ${
                    showFeedback
                        ? 'opacity-100 translate-y-0 animate-pulse hover:animate-none'
                        : 'opacity-0 translate-y-4 pointer-events-none'
                }`}>
                    <NeubrutalistFeedbackCard />
                </div>

                {/* Final Decorative Divider Before CTA */}
                <div className="relative py-4">
                    <div className="flex justify-center items-center space-x-2">
                        <div className="w-12 h-3 bg-red-400 border-2 border-black transform rotate-6 shadow-lg"></div>
                        <div className="w-8 h-8 bg-blue-400 border-2 border-black rounded-full shadow-lg"></div>
                        <div className="w-6 h-6 bg-yellow-400 border-2 border-black transform rotate-45 shadow-lg"></div>
                        <div className="w-10 h-4 bg-green-400 border-2 border-black transform -rotate-12 shadow-lg"></div>
                        <div className="w-5 h-7 bg-purple-400 border-2 border-black transform rotate-3 shadow-lg"></div>
                    </div>
                </div>

                <CallToAction />
            </div>
            
            {/* Sticky footer with lower z-index */}
            <Footer />
        </div>
    )
}
